import { AxiosError, AxiosResponse } from 'axios';
import { LOCAL_STORAGE_TOKEN_KEY } from '../common/constants';
import showNotification from '../components/extras/showNotification';
import { IErrorType } from '../types/error-type';
import { ILoginProps } from '../types/login-props-type';
import { Pagination } from '../types/pagination-type';
import { IUser } from '../types/user-type';
import { Network } from '../utils/Network';

interface IAuthenticationService {
	updateCustomer(idCliente: string, data: any): Promise<void>;
	enableUser(idCliente: string, flag: boolean): Promise<void>;
	getAdminUser(): Promise<AxiosResponse<any[], AxiosError>>;
	logout(arg0: string | null): Promise<void>;
	login: (props: ILoginProps) => Promise<AxiosResponse<Partial<IUser>, AxiosError>>;
	retrieveCustomer: (token: string) => Promise<AxiosResponse<Partial<IUser>, AxiosError>>;
	saveOneSignalId: (oneSignalToken: string) => Promise<void>;
	getUsers: (
		filter: string,
		currentPage: number,
		itemsForPage: number,
		enabledOnly?: boolean,
	) => Promise<AxiosResponse<Pagination<any[], 'result'>, AxiosError>>;
	createCustomer(data: any): Promise<any>;
}

const AuthenticationService: IAuthenticationService = {
	login: async ({ username, password }) => {
		const body = {
			username,
			password,
		};
		try {
			const response = await Network.post('/api/login', body);
			localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, response.data.jwtToken);
			return await AuthenticationService.retrieveCustomer(response.data?.jwtToken);
		} catch (error: IErrorType | any) {
			showNotification('Errore', error.errorMessage, 'danger');
			throw new Error('Errore durante la login');
		}
	},
	retrieveCustomer: (token) => {
		try {
			const response = Network.get(`/api/retrieveCustomer?jwt=${token}`, {
				headers: { 'X-Auth-Token': token },
			});
			return response;
		} catch (e: IErrorType | any) {
			console.log('Errore chiamata retrieve use', e);
			throw new Error('Sessione scaduta');
		}
	},
	logout: async (token) => {
		try {
			await Network.get('/public/authentication/logout', { params: { jwt: token } });
			localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY);
		} catch (error: IErrorType | any) {
			showNotification('Errore', 'Errore generico, aggiorna la pagina', 'danger');
		}
	},
	saveOneSignalId: async (oneSignalToken) => {
		try {
			await Network.put(
				'/public/authentication/update-one-signal?oneSignalToken=' + oneSignalToken,
			);
		} catch (error: IErrorType | any) {
			console.error('Errore durante il salvataggio del token di OneSignal', error);
		}
	},
	getAdminUser: async () => {
		try {
			const response = await Network.get('/v1/api/users-admin');
			return response.data;
		} catch (error: IErrorType | any) {
			showNotification('Errore', error.errorMessage, 'danger');
			throw new Error('Errore durante il recupero degli utenti');
		}
	},
	getUsers: async (filter, currentPage, itemsForPage, enabledOnly = true) => {
		try {
			const response = await Network.get('/api/admin/getAllCustomerByFilter', {
				params: { filter, currentPage, itemsForPage, enabledOnly },
			});
			return response;
		} catch (error: IErrorType | any) {
			showNotification('Errore', error.errorMessage, 'danger');
			throw new Error('Errore durante il recupero degli utenti');
		}
	},
	enableUser: async (idCliente, flag) => {
		try {
			await Network.get(`/api/admin/enabledCustomer/customerId/${idCliente}?flag=${flag}`);
		} catch (error: IErrorType | any) {
			showNotification('Errore', error.errorMessage, 'danger');
			throw new Error("Errore durante l'abilitazione dell'utente");
		}
	},
	updateCustomer: async (idCliente, data) => {
		try {
			await Network.post(`/api/admin/updateCustomer/customerId/${idCliente}`, data);
		} catch (error: IErrorType | any) {
			showNotification('Errore', error.errorMessage, 'danger');
			throw new Error('Errore durante l’aggiornamento del cliente');
		}
	},
	createCustomer(data: any) {
		return Network.post('/api/admin/createCustomer', data);
	},
};

export default AuthenticationService;
